import { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useAuthStore } from './stores/authStore'
import { useAuditorStore } from './stores/auditorStore'
import printerService from './services/printerService'
import { apiClient } from './services/api'
import { Layout } from './components/layout/Layout'
import { POSLayout } from './components/layout/POSLayout'
import { LoginForm } from './components/auth/LoginForm'
import { RegisterForm } from './components/auth/RegisterForm'
import { ForgotPassword } from './pages/ForgotPassword'
import { ResetPassword } from './pages/ResetPassword'
import { Onboarding } from './pages/Onboarding'
import { Dashboard } from './pages/Dashboard'
import { POSSystemNew } from './pages/POSSystemNew'
import { CustomerManagement } from './pages/CustomerManagement'
import { OrdersManagement } from './pages/OrdersManagement'
import { Reports } from './pages/Reports'
import { Settings } from './pages/Settings'
import { Profile } from './pages/Profile'
import { ProductManagement } from './components/inventory/ProductManagement'
import { CategoryManagement } from './components/inventory/CategoryManagement'
import { UnitManagement } from './components/inventory/UnitManagement'
import { StockManagement } from './components/inventory/StockManagement'
import { ImportExport } from './components/inventory/ImportExport'
import { ExpenseManagement } from './components/expenses'
import { SupplierManagement } from './components/suppliers'
import { PurchaseManagement } from './components/purchases/PurchaseManagement'
import { PurchaseForm } from './pages/PurchaseForm'
import { PurchaseDetail } from './pages/PurchaseDetail'
import { ProductForm } from './pages/ProductForm'
import { CategoryForm } from './pages/CategoryForm'
import Invoices from './pages/Invoices'
import InvoiceForm from './pages/InvoiceForm'
import InvoiceDetail from './pages/InvoiceDetail'
import { UserManagement } from './pages/UserManagement'
import { RoleManagement } from './pages/RoleManagement'
import { AuditorManagement } from './pages/AuditorManagement'
import { AuditorInvitation } from './pages/auditor/AuditorInvitation'
import { ErrorBoundary } from './components/ErrorBoundary'
import { FullScreenLoading } from './components/LoadingSpinner'
import { ThemeProvider } from './contexts/ThemeContext'
import { SyncProgress } from './components/ui/SyncProgress'
import { VersionInfo } from './components/VersionInfo'
import './index.css'

function App() {
  const { isAuthenticated, user, getCurrentUser, isLoading } = useAuthStore()
  const { isAuditor } = useAuditorStore()
  const [showSyncProgress, setShowSyncProgress] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024)
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024)
    }
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  
  // Debug: Add a simple test element to check if Tailwind is working
  console.log('App rendering - checking Tailwind CSS loading...')

  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (token && !isAuthenticated) {
      getCurrentUser()
    }
  }, [isAuthenticated, getCurrentUser])

  // Load printer settings when user is authenticated
  useEffect(() => {
    const loadPrinterSettings = async () => {
      if (!isAuthenticated || !user) return

      try {
        const settingsResponse = await apiClient.getSettings()

        // Update printer service with loaded print settings
        if (settingsResponse.print) {
          printerService.setSettings({
            format: settingsResponse.print.invoice_format || 'thermal',
            showLogo: settingsResponse.print.show_logo ?? true,
            showTerms: settingsResponse.print.show_terms ?? true,
            autoPrint: settingsResponse.print.auto_print ?? false,
            copiesCount: settingsResponse.print.copies_count || 1,
            itemsPerPage: settingsResponse.print.items_per_page || 15
          })
        }

        // Update printer service with business info
        if (settingsResponse.business) {
          printerService.setBusinessInfo({
            name: settingsResponse.business.business_name || '',
            address: settingsResponse.business.address || '',
            phone: settingsResponse.business.phone || '',
            email: settingsResponse.business.email || '',
            gstNumber: settingsResponse.business.gst_number || '',
            website: settingsResponse.business.website || undefined,
            state: settingsResponse.business.state || undefined
          })
        }

        console.log('Printer settings loaded:', settingsResponse.print)
      } catch (error) {
        console.error('Failed to load printer settings:', error)
      }
    }

    loadPrinterSettings()
  }, [isAuthenticated, user])

  // Handle initial sync trigger for Electron app
  useEffect(() => {
    if (isAuthenticated && window.isElectron) {
      // Listen for sync trigger from main process
      if (window.electronAPI?.on) {
        window.electronAPI.on('trigger-sync', async () => {
          setShowSyncProgress(true)
          
          // Actually trigger the sync process
          try {
            // Import and trigger sync
            const { apiClient } = await import('./services/api')
            await apiClient.syncWithBackend()
          } catch (error) {
            console.error('Failed to trigger sync:', error)
            // Send error progress update
            if (window.electronAPI?.send) {
              window.electronAPI.send('sync-progress-update', {
                isLoading: false,
                progress: 0,
                currentStep: '',
                totalSteps: 3,
                currentStepIndex: 0,
                error: `Sync failed: ${error.message}`,
                success: false
              })
            }
          }
        })
      }
    }

    return () => {
      if (window.electronAPI?.removeAllListeners) {
        window.electronAPI.removeAllListeners('trigger-sync')
      }
    }
  }, [isAuthenticated])

  if (isLoading) {
    return <FullScreenLoading message="Loading digiBills" />
  }

  if (!isAuthenticated) {
    return (
      <Router>
        <div className="min-h-screen">
          <Routes>
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Public auditor invitation acceptance page */}
            <Route path="/auditor/accept" element={<AuditorInvitation />} />
            <Route path="*" element={<LoginForm />} />
          </Routes>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              className: '',
              style: {
                background: '#f97316',
                color: '#fff',
              },
            }}
          />
        </div>
      </Router>
    )
  }

  // Check if user needs to complete onboarding (skip for auditors)
  const needsOnboarding = isAuthenticated && user && !user.onboarding_completed && !isAuditor

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <Router>
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
            <Routes>
              {/* Onboarding route */}
              <Route path="/onboarding" element={<Onboarding />} />

              {/* POS System with dedicated layout */}
              <Route path="/pos" element={<POSLayout />}>
                <Route index element={<POSSystemNew />} />
              </Route>

              {/* Redirect to onboarding if needed - MUST come after specific routes */}
              {needsOnboarding && <Route path="*" element={<Navigate to="/onboarding" replace />} />}

              {/* Main dashboard and admin routes */}
              <Route path="/" element={<Layout />}>
                <Route index element={<Dashboard />} />
                <Route path="products" element={<ProductManagement />} />
                <Route path="products/new" element={<ProductForm />} />
                <Route path="products/edit/:id" element={<ProductForm />} />
                <Route path="categories" element={<CategoryManagement />} />
                <Route path="categories/new" element={<CategoryForm />} />
                <Route path="categories/edit/:id" element={<CategoryForm />} />
                <Route path="units" element={<UnitManagement />} />
                <Route path="stock" element={<StockManagement />} />
                <Route path="import-export" element={<ImportExport />} />

                {/* Expenses */}
                <Route path="expenses" element={<ExpenseManagement />} />

                {/* Suppliers */}
                <Route path="suppliers" element={<SupplierManagement />} />

                {/* Purchases */}
                <Route path="purchases" element={<PurchaseManagement />} />
                <Route path="purchases/new" element={<PurchaseForm />} />
                <Route path="purchases/:id" element={<PurchaseDetail />} />
                <Route path="purchases/:id/edit" element={<PurchaseForm />} />

                {/* Reports */}
                <Route path="reports" element={<Reports />} />

                {/* Invoices */}
                <Route path="invoices" element={<Invoices />} />
                <Route path="invoices/new" element={<InvoiceForm />} />
                <Route path="invoices/:id" element={<InvoiceDetail />} />
                <Route path="invoices/:id/edit" element={<InvoiceForm />} />

                {/* Other admin routes */}
                <Route path="orders" element={<OrdersManagement />} />
                <Route path="customers" element={<CustomerManagement />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="roles" element={<RoleManagement />} />
                <Route path="settings" element={<Settings />} />
                <Route path="settings/auditors" element={<AuditorManagement />} />
                <Route path="profile" element={<Profile />} />

                {/* Redirect any unknown routes to dashboard */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
            
            <Toaster 
              position={isMobile ? "bottom-center" : "top-right"}
              toastOptions={{
                duration: isMobile ? 1500 : 4000,
                className: isMobile ? 'text-xs' : '',
                style: isMobile ? {
                  fontSize: '12px',
                  padding: '8px 12px',
                  marginBottom: '85px',
                  maxWidth: '280px',
                  borderRadius: '6px'
                } : {},
                success: {
                  style: {
                    background: '#10b981',
                    color: '#fff',
                    ...(isMobile ? {
                      fontSize: '12px',
                      padding: '8px 12px',
                      marginBottom: '85px',
                      maxWidth: '280px',
                      borderRadius: '6px'
                    } : {})
                  },
                },
                error: {
                  style: {
                    background: '#ef4444',
                    color: '#fff',
                    ...(isMobile ? {
                      fontSize: '12px',
                      padding: '8px 12px',
                      marginBottom: '85px',
                      maxWidth: '280px',
                      borderRadius: '6px'
                    } : {})
                  },
                },
              }}
            />
            
            {/* Sync Progress Modal */}
            <SyncProgress
              isVisible={showSyncProgress}
              onComplete={() => setShowSyncProgress(false)}
            />

            {/* Version Info - Shows build timestamp and version */}
            <VersionInfo />
          </div>
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  )
}

// Coming Soon Component
const ComingSoonPage = ({ title }: { title: string }) => {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <div className="text-6xl mb-6">🚧</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{title}</h1>
        <p className="text-gray-600 mb-8">This feature is coming soon!</p>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 max-w-md mx-auto">
          <h3 className="font-semibold text-orange-900 mb-2">What's Next?</h3>
          <p className="text-orange-700 text-sm">
            We're working hard to bring you amazing {title.toLowerCase()} features. 
            Stay tuned for updates!
          </p>
        </div>
      </div>
    </div>
  )
}

export default App